"use client";

import React, { useEffect, useRef, useState } from "react";
import AccordionPanel from "./AccordionPanel";
import {
  ClassResponse,
  ContentResponse,
} from "../../services/api.response.service";
import { useSelector } from "react-redux";
import { IRootState } from "../../redux/states";
import { getLanguageByDefault } from "../../services/app.service";
import { Mixpanel } from "../../services/mixpanel";
import Api from "../../services/api.service";
import { toInteger } from "lodash";
import {
  ContentMediaBadges,
  contentHasMediaBadges,
} from "./ContentMediaBadges";
import { mergeCourseTopicsIntoList } from "../../utils/extraFunctions";

export default function CoursesSection({
  classDetails,
  contentId,
  isShortForm = false,
  useStructureEndpoint = false,
  handleSetCourse,
}: {
  classDetails: ClassResponse;
  contentId?: number;
  isShortForm?: boolean | false;
  useStructureEndpoint?: boolean;
  handleSetCourse: (id: string, title: string) => void;
}) {
  const getLanguage = getLanguageByDefault();
  const [activeSector, setActiveSector] = React.useState<number>(-1);

  const stateAll = useSelector((state: IRootState) => state);
  const currentUser = stateAll.setting.user!;

  const [contents, setContents] = useState<ContentResponse[] | []>([]);
  const [currentTopicId, setCurrentTopicId] = useState<number | null>(
    contentId ?? null,
  );
  const [loadingCourseIds, setLoadingCourseIds] = useState<
    Record<number, boolean>
  >({});
  const [errorCourseIds, setErrorCourseIds] = useState<Record<number, boolean>>(
    {},
  );
  const [isLoadingStructure, setIsLoadingStructure] = useState(false);
  const [structureError, setStructureError] = useState(false);
  const topicsFetchedRef = useRef<Set<number>>(new Set());

  function hasTopics(content: ContentResponse) {
    return content.total_topics > 0 || (content.topics?.length ?? 0) > 0;
  }

  function shouldFetchTopics(course: ContentResponse) {
    return (
      hasTopics(course) &&
      !topicsFetchedRef.current.has(course.id) &&
      (!course.topics || course.topics.length === 0)
    );
  }

  function syncEmbeddedTopicsRef(courses: ContentResponse[]) {
    courses.forEach((c) => {
      if (c.topics && c.topics.length > 0) {
        topicsFetchedRef.current.add(c.id);
      }
    });
  }

  function setTheCourse(id: string, title: string) {
    const input = document.getElementById(
      `topic-check-${id}`,
    ) as HTMLInputElement;
    if (input) {
      input.checked = true;
    }

    Mixpanel.track("Tap Course Outline item", {
      with: currentUser?.id ? "Auth" : "Guest",
      user_id: currentUser?.id,
      course_id: id,
      course_name: title,
      ...(classDetails?.content_provider_id
        ? { content_provider_id: classDetails?.content_provider_id }
        : {}),
    });
    handleSetCourse && handleSetCourse(id, title);
  }

  const getTopics = async (
    contentIdParam: string | number,
  ): Promise<ContentResponse[] | null> => {
    const contentsObj = await Api.getCourseTopics(toInteger(contentIdParam));
    if (!contentsObj) {
      return null;
    }
    const theTopics = contentsObj.data.data as ContentResponse[];

    await Promise.all(
      theTopics.map(async (topic) => {
        if (hasTopics(topic)) {
          const nested = await getTopics(topic.id);
          if (nested) {
            topic.topics = nested;
          }
        }
      }),
    );
    return theTopics;
  };

  const hydrateCourseTopics = async (course: ContentResponse) => {
    topicsFetchedRef.current.add(course.id);
    setLoadingCourseIds((prev) => ({ ...prev, [course.id]: true }));
    setErrorCourseIds((prev) => ({ ...prev, [course.id]: false }));
    try {
      const topics = await getTopics(course.id);
      setContents((prev) =>
        mergeCourseTopicsIntoList(prev, course.id, topics ?? []),
      );
      setErrorCourseIds((prev) => ({ ...prev, [course.id]: false }));
    } catch {
      setContents((prev) => mergeCourseTopicsIntoList(prev, course.id, []));
      setErrorCourseIds((prev) => ({ ...prev, [course.id]: true }));
    } finally {
      setLoadingCourseIds((prev) => ({ ...prev, [course.id]: false }));
    }
  };

  const runBackgroundHydrate = async (courses: ContentResponse[]) => {
    const toHydrate = courses.filter((c) => shouldFetchTopics(c));
    const priority = contentId
      ? toHydrate.filter((c) => c.id === contentId)
      : [];
    const rest = toHydrate.filter((c) => c.id !== contentId);
    const ordered = [...priority, ...rest];

    await Promise.all(ordered.map((course) => hydrateCourseTopics(course)));
  };

  const retryCourseTopics = (course: ContentResponse) => {
    topicsFetchedRef.current.delete(course.id);
    void hydrateCourseTopics(course);
  };

  const getContents = async (classId: string | number) => {
    if (useStructureEndpoint) {
      setIsLoadingStructure(true);
      setStructureError(false);
      setContents([]);
    }
    try {
      const contentsObj = useStructureEndpoint
        ? await Api.getClassCourseStructure(toInteger(classId))
        : await Api.getClassCourses(toInteger(classId));
      if (!contentsObj) {
        if (useStructureEndpoint) {
          setStructureError(true);
          setContents([]);
        }
        return null;
      }
      const theCourses = contentsObj.data.data as ContentResponse[] | [];

      syncEmbeddedTopicsRef(theCourses);
      setContents(theCourses);
      setErrorCourseIds({});
      setStructureError(false);
      if (!useStructureEndpoint) {
        void runBackgroundHydrate(theCourses);
      }

      return theCourses;
    } catch {
      if (useStructureEndpoint) {
        setStructureError(true);
        setContents([]);
      }
      return null;
    } finally {
      if (useStructureEndpoint) {
        setIsLoadingStructure(false);
      }
    }
  };

  useEffect(() => {
    topicsFetchedRef.current = new Set();
    setErrorCourseIds({});
    setStructureError(false);
  }, [classDetails?.id]);

  function getTopicView(topic: ContentResponse, index: number) {
    const isCurrent = topic.id === currentTopicId;
    const isCompleted = (topic.user_points?.length ?? 0) > 0;
    const rowClass = [
      "topic-row",
      "row",
      "col-12",
      "mt-1",
      isCurrent && "topic-row--current",
      isCompleted && !isCurrent && "topic-row--completed",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div key={topic.id} className={rowClass}>
        <div className={"hover-underline row col-12 "}>
          <div className={`${isShortForm ? "col-sm-12" : "col-sm-8"}`}>
            {isShortForm && (
              <input
                id={`topic-check-${topic.id}`}
                type={"checkbox"}
                className="topic-row__checkbox"
                checked={isCompleted}
                style={{
                  cursor: "pointer",
                  marginRight: "10px",
                }}
                onChange={() => {}}
              />
            )}

            <span
              className="topic-fw text-black justify-content-start"
              style={{
                cursor: "pointer",
                fontWeight: isCurrent ? "600" : "400",
              }}
              onClick={() => {
                setTheCourse(String(topic?.id), topic.title);
                setCurrentTopicId(topic?.id);
              }}
            >
              {index + 1}. {topic.title}
            </span>
          </div>

          <div className={`${isShortForm ? "col-sm-12" : "col-sm-4"}`}>
            <ContentMediaBadges
              content={topic}
              isShortForm={isShortForm}
              lang={{
                watch: getLanguage.watch,
                listen: getLanguage.listen,
                read: getLanguage.read,
              }}
            />
          </div>
        </div>
        {hasTopics(topic) && (
          <div className="m-1 mx-3">
            {topic.topics?.map((subTopic, subIndex) =>
              getTopicView(subTopic, subIndex),
            )}
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    if (classDetails?.id) {
      getContents(classDetails.id).catch((err) => {
        console.error("Error fetching contents", err);
      });
    }
  }, [classDetails, useStructureEndpoint]);

  const showTopicsLoading = (course: ContentResponse) =>
    hasTopics(course) &&
    loadingCourseIds[course.id] &&
    (!course.topics || course.topics.length === 0);

  const showTopicsError = (course: ContentResponse) =>
    hasTopics(course) &&
    !!errorCourseIds[course.id] &&
    !loadingCourseIds[course.id];

  const showTopicsEmpty = (course: ContentResponse) =>
    hasTopics(course) &&
    topicsFetchedRef.current.has(course.id) &&
    !loadingCourseIds[course.id] &&
    !errorCourseIds[course.id] &&
    (!course.topics || course.topics.length === 0);

  return (
    <div className="col-12">
      {useStructureEndpoint && isLoadingStructure && (
        <div className="text-muted small py-2" role="status">
          {getLanguage.loadingTopics}
        </div>
      )}
      {useStructureEndpoint && structureError && !isLoadingStructure && (
        <div className="small py-2" role="alert">
          <span className="text-muted d-block mb-2">
            {getLanguage.topicsLoadError}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              if (classDetails?.id) {
                void getContents(classDetails.id);
              }
            }}
          >
            {getLanguage.retry}
          </button>
        </div>
      )}
      {!isLoadingStructure &&
        contents &&
        contents.length > 0 &&
        contents.map((course, i) => (
          <AccordionPanel
            key={course.id}
            content={course}
            title={course.title}
            headerBadges={
              contentHasMediaBadges(course) ? (
                <ContentMediaBadges
                  content={course}
                  isShortForm={isShortForm}
                  justifyMain="start"
                  lang={{
                    watch: getLanguage.watch,
                    listen: getLanguage.listen,
                    read: getLanguage.read,
                  }}
                />
              ) : undefined
            }
            subTitle={
              hasTopics(course)
                ? `${course.total_topics || course.topics?.length} ${getLanguage.topics} `
                : isShortForm
                  ? ""
                  : course.description
            }
            showItems={hasTopics(course)}
            setIsOpen={() =>
              setActiveSector((previous) => (previous == i ? -1 : i))
            }
            isOpen={activeSector == i}
            handleHeadClick={() => {
              handleSetCourse &&
                handleSetCourse(String(course?.id), course.title);
            }}
          >
            <div>
              <div className="m-4">
                {showTopicsLoading(course) && (
                  <div className="text-muted small py-2" role="status">
                    {getLanguage.loadingTopics}
                  </div>
                )}
                {showTopicsError(course) && (
                  <div className="small py-2" role="alert">
                    <span className="text-muted d-block mb-2">
                      {getLanguage.topicsLoadError}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => retryCourseTopics(course)}
                    >
                      {getLanguage.retry}
                    </button>
                  </div>
                )}
                {showTopicsEmpty(course) && (
                  <div className="text-muted small py-2" role="status">
                    {getLanguage.noTopic}
                  </div>
                )}
                {hasTopics(course) &&
                  course.topics &&
                  course.topics.length > 0 &&
                  course.topics.map((topic, index) =>
                    getTopicView(topic, index),
                  )}
              </div>
            </div>
          </AccordionPanel>
        ))}
    </div>
  );
}
